// Shared domain types for the Havik player SDK.

export type DrmSystem = 'widevine' | 'fairplay';

export interface WidevineInfo {
  licenseUrl: string;
}
export interface FairPlayInfo {
  licenseUrl: string;
  certificateUrl: string;
}
export interface DrmInfo {
  widevine?: WidevineInfo;
  fairplay?: FairPlayInfo;
}

/**
 * The per-match playback credential returned by `GET /v1/playback/{urn}`,
 * normalized for the SDK. `manifestUrl` is an unsigned HLS `.m3u8`; the DRM
 * license URLs are pre-signed by havik-streams (the signed query string IS the
 * token — never rewrite them).
 */
export interface StreamDescriptor {
  matchUrn: string;
  protocol: 'hls';
  /** Producer-set DRM decision. When false, init no EME/CDM at all. */
  drmEnabled: boolean;
  manifestUrl: string;
  /** Present only when drmEnabled. */
  drm?: DrmInfo;
  serverTime: string;
  /** Present only while the match is upcoming. */
  liveStartsAt?: string;
  /** Filled by the player once a media playlist with EXT-X-PART-INF is seen. */
  isLowLatency?: boolean;
  /**
   * Analytics session binding minted by havik-streams (present when the
   * server has analytics enabled). The sid is an opaque per-playback-session
   * id — non-secret by design (DRM remains the access gate) — echoed in CMCD
   * query params on CDN requests and carried by QoE beacons. It lives only in
   * memory for the duration of the session and is never persisted.
   */
  analytics?: { sid?: string };
  /** Any unrecognized top-level fields from the playback response (forward-compat). */
  extensions?: Record<string, unknown>;
}

export interface Credential {
  /** A publishable api-key, e.g. `pk_live_…` / `pk_test_…`. */
  apiKey: string;
}

/**
 * A credential, or a function that produces one. The function form lets a host
 * plug in a future short-lived-token mint service without an API change; it is
 * invoked per request so refresh works transparently.
 */
export type CredentialSource = Credential | (() => Credential | Promise<Credential>);

/** @internal */
export async function resolveCredential(source: CredentialSource): Promise<Credential> {
  const cred = typeof source === 'function' ? await source() : source;
  if (!cred || !cred.apiKey) {
    throw new Error('havik-player: credential is missing an apiKey');
  }
  return cred;
}

export type MatchLiveStatus = 'upcoming' | 'live' | 'ended';

export interface MatchStatus {
  matchUrn: string;
  matchName?: string;
  status: MatchLiveStatus;
  datePlannedStart?: string;
}
